import React, {useMemo, useState, useCallback} from 'react';
import moment from 'moment';
import strings from '../../utils/strings';
import './styles.scss';

const StatusIcon = function ({kind}) {
	var inner = null;

	if (kind === 'tick') inner = <div className="tickmark large">L</div>;
	else if (kind === 'continue')
		inner = <div className="continue large">./..</div>;
	else inner = <div className="crossmark large">X</div>;

	return <div className="lit-status-cell">{inner}</div>;
};

const safe = function (v, fallback) {
	var fb = typeof fallback === 'undefined' ? '—' : fallback;
	return v === null || typeof v === 'undefined' || v === '' ? fb : String(v);
};

const formatDate = function (d, fmt) {
	var f = typeof fmt === 'undefined' ? 'LL' : fmt;
	return d ? moment(d).format(f) : '—';
};

const isInvoiced = function (t) {
	return !!(
		t &&
		t.isCompleted &&
		t.completedAt &&
		t.lastInvoicedAt &&
		moment(t.completedAt).isBefore(moment(t.lastInvoicedAt))
	);
};

// eslint-disable-next-line react/no-multi-comp
export default function ViewSeTests(props) {
	var testsForDumping = props.testsForDumping || [];
	var isForSeView = !!props.isForSeView;
	var closeWindow = props.closeWindow;
	var onDumpSingleTest = props.onDumpSingleTest;
	var onDumpAllTests = props.onDumpAllTests;

	var _useState = useState({
		search: '',
		type: 'all', // all | <type>
		completed: 'all', // all | yes | no

		// Only used when !isForSeView
		invoice: 'all', // all | invoiced | notInvoiced | inProgress
		reason: 'all', // all | deleted | old
	});
	var filters = _useState[0];
	var setFilters = _useState[1];

	var typeOptions = useMemo(
		function () {
			var set = {};
			for (var i = 0; i < testsForDumping.length; i++) {
				var t = testsForDumping[i];
				if (t && t.type) set[t.type] = true;
			}
			var arr = Object.keys(set);
			arr.sort(function (a, b) {
				return String(a).localeCompare(String(b));
			});
			return arr;
		},
		[testsForDumping]
	);

	var filteredTests = useMemo(
		function () {
			var q = String(filters.search || '')
				.trim()
				.toLowerCase();

			var list = testsForDumping.filter(function (t) {
				if (!t) return false;

				// Type filter
				if (filters.type !== 'all' && t.type !== filters.type)
					return false;

				// Completed filter
				if (filters.completed === 'yes' && !t.isCompleted) return false;
				if (filters.completed === 'no' && t.isCompleted) return false;

				// Non-SE-only filters
				if (!isForSeView) {
					if (filters.invoice !== 'all') {
						var invoiced = isInvoiced(t);
						var inProgress = !t.isCompleted;
						var notInvoiced = t.isCompleted && !invoiced;

						if (filters.invoice === 'invoiced' && !invoiced)
							return false;
						if (filters.invoice === 'notInvoiced' && !notInvoiced)
							return false;
						if (filters.invoice === 'inProgress' && !inProgress)
							return false;
					}

					if (filters.reason !== 'all') {
						var reason = t.deleted ? 'deleted' : 'old';
						if (filters.reason !== reason) return false;
					}
				}

				// Search filter (name/key/ids/type)
				if (q) {
					var parts = [];
					if (t.firstName) parts.push(t.firstName);
					if (t.lastName) parts.push(t.lastName);
					if (t.keyString) parts.push(t.keyString);
					if (t.teacherId) parts.push(t.teacherId);
					if (t.token) parts.push(t.token);
					if (t.type) parts.push(t.type);

					var haystack = parts.join(' ').toLowerCase();
					if (haystack.indexOf(q) === -1) return false;
				}

				return true;
			});

			// newest usedAt first
			list.sort(function (a, b) {
				var ad = a && a.usedAt ? +new Date(a.usedAt) : 0;
				var bd = b && b.usedAt ? +new Date(b.usedAt) : 0;
				return bd - ad;
			});

			return list;
		},
		[testsForDumping, filters, isForSeView]
	);

	var hasActiveFilters = useMemo(
		function () {
			return (
				String(filters.search || '').trim() !== '' ||
				filters.type !== 'all' ||
				filters.completed !== 'all' ||
				(!isForSeView &&
					(filters.invoice !== 'all' || filters.reason !== 'all'))
			);
		},
		[filters, isForSeView]
	);

	var clearFilters = useCallback(
		function () {
			setFilters({
				search: '',
				type: 'all',
				completed: 'all',
				invoice: 'all',
				reason: 'all',
			});
		},
		[setFilters]
	);

	var columns = useMemo(
		function () {
			var cols = [
				{
					key: 'name',
					header: strings.common.firstname + ', ' + strings.common.lastname,
					render: function (t) {
						if (!t.firstName || !t.lastName) {
							return (
								<span className="lit-pill lit-pill--muted">
									{strings.users.deletedUser}
								</span>
							);
						}
						return t.firstName + ', ' + t.lastName;
					},
				},
			];

			if (isForSeView) {
				cols.push(
					{
						key: 'subscription',
						header: strings.users.subscription,
						render: function (t) {
							return safe(t.teacherId, '--Unknown---');
						},
					},
					{
						key: 'studentId',
						header: strings.auth.studentID,
						render: function (t) {
							return safe(t.token, '--Unknown---');
						},
					}
				);
			}

			cols.push(
				{
					key: 'testkey',
					header: strings.testManagement.testkey,
					render: function (t) {
						return safe(t.keyString);
					},
				},
				{
					key: 'type',
					header: strings.common.type,
					render: function (t) {
						return safe(t.type);
					},
				}
			);

			if (!isForSeView) {
				cols.push({
					key: 'invoiced',
					header: 'Fakturert',
					render: function (t) {
						if (!t.isCompleted)
							return <StatusIcon kind="continue" />;
						return isInvoiced(t) ? (
							<StatusIcon kind="tick" />
						) : (
							<StatusIcon kind="cross" />
						);
					},
				});
			}

			cols.push({
				key: 'usedOn',
				header: strings.testManagement.usedon,
				render: function (t) {
					return formatDate(t.usedAt, 'LL');
				},
			});

			cols.push({
				key: 'completed',
				header: strings.common.completed,
				render: function (t) {
					return t.isCompleted ? (
						<StatusIcon kind="tick" />
					) : (
						<StatusIcon kind="cross" />
					);
				},
			});

			if (!isForSeView) {
				cols.push(
					{
						key: 'reason',
						header: strings.common.reason,
						render: function (t) {
							return t.deleted
								? strings.testManagement.testdeletedbyuser
								: strings.testManagement.testolderthan3years;
						},
					},
					{
						key: 'action',
						header: strings.common.action,
						render: function (t) {
							return (
								<button
									className="lit-btn bg-negative"
									onClick={function () {
										if (
											window.confirm(
												strings.testManagement.warning_deleteSingleTest
											)
										) {
											if (
												typeof onDumpSingleTest ===
												'function'
											) {
												onDumpSingleTest(
													t.id,
													t.type,
													t.keyString
												);
											}
										}
									}}
								>
									{strings.common.delete}
								</button>
							);
						},
					}
				);
			}

			return cols;
		},
		[isForSeView, onDumpSingleTest]
	);

	return (
		<div className="lit-modal" onClick={closeWindow}>
			<div
				className="lit-modal__island"
				onClick={function (e) {
					e.stopPropagation();
				}}
			>
				<h3>
					{!isForSeView
						? strings.users.deleteOldTests
						: strings.controlPanel.seNormingTests}
				</h3>

				{/* Filters */}
				<div className="lit-tests__filters-new">
					<input
						className="lit-input-new"
						type="search"
						placeholder={strings.testManagement.seTestsSearchPlaceholder}
						value={filters.search}
						onChange={function (e) {
							// old React pooled event fix: capture value immediately
							var value = e.target.value;
							setFilters(function (f) {
								return Object.assign({}, f, {search: value});
							});
						}}
					/>

					<select
						className="lit-select"
						value={filters.type}
						onChange={function (e) {
							var value = e.target.value;
							setFilters(function (f) {
								return Object.assign({}, f, {type: value});
							});
						}}
					>
						<option value="all">{strings.users.allTypes}</option>
						{typeOptions.map(function (t) {
							return (
								<option key={t} value={t}>
									{t}
								</option>
							);
						})}
					</select>

					<select
						className="lit-select"
						value={filters.completed}
						onChange={function (e) {
							var value = e.target.value;
							setFilters(function (f) {
								return Object.assign({}, f, {completed: value});
							});
						}}
					>
						<option value="all">{strings.common.all}</option>
						<option value="yes">{strings.common.completed}</option>
						<option value="no">{strings.common.notCompleted}</option>
					</select>

					{!isForSeView ? (
						<>
							<select
								className="lit-select"
								value={filters.invoice}
								onChange={function (e) {
									var value = e.target.value;
									setFilters(function (f) {
										return Object.assign({}, f, {
											invoice: value,
										});
									});
								}}
							>
								<option value="all">{strings.testManagement.allInvoiceStates}</option>
								<option value="invoiced">{strings.users.invoiced}</option>
								<option value="notInvoiced">
									{strings.users.notInvoiced}
								</option>
								<option value="inProgress">{strings.common.inProgress}</option>
							</select>

							<select
								className="lit-select"
								value={filters.reason}
								onChange={function (e) {
									var value = e.target.value;
									setFilters(function (f) {
										return Object.assign({}, f, {
											reason: value,
										});
									});
								}}
							>
								<option value="all">{strings.testManagement.allReasons}</option>
								<option value="deleted">{strings.testManagement.deletedByUser}</option>
								<option value="old">{strings.testManagement.olderThan3Years}</option>
							</select>
						</>
					) : null}

					{hasActiveFilters ? (
						<button className="lit-btn" onClick={clearFilters}>
							{strings.common.clear}
						</button>
					) : null}

				<div className="lit-tests__count-new">
					{strings.formatString(strings.common.showingXofY, filteredTests.length, testsForDumping.length)}
				</div>
				</div>

				<table className="lit-tests">
					<thead>
						<tr>
							{columns.map(function (c) {
								return (
									<th
										key={c.key}
										className="lit-test__header"
									>
										{c.header}
									</th>
								);
							})}
						</tr>
					</thead>

					<tbody>
						{filteredTests.map(function (t) {
							return (
								<tr className="lit-list__item" key={t.id}>
									{columns.map(function (c) {
										return (
											<td
												key={c.key}
												className="lit-test__col"
											>
												{c.render(t)}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>

				{/* <div className="lit-deleteAll">
					<button
						className="lit-btn bg-negative"
						onClick={function () {
							if (window.confirm(strings.testManagement.warning_deleteAllTest)) {
								if (typeof onDumpAllTests === 'function') {
									onDumpAllTests(testsForDumping);
								}
							}
						}}
					>
						{strings.testManagement.deleteall}
					</button>
				</div> */}
			</div>
		</div>
	);
}
